import { useState } from 'react';

import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { useAddPubsubEventMutation } from '../../projects/services/projects.api';
import { useCreateBusinessRuleMutation, useUpdateBusinessRuleMutation } from '../services/businessRules.api';
import { DEFAULT_NOTIFICATION_CONFIG } from '../types/notificationConfig';
import { NOTIFICATION_RULE_NAME } from './useNotificationConfig';

interface UseActivateNotificationParams {
  projectId: number;
  existingBusinessRuleId?: number | null;
  hasPubsubEvent?: boolean;
  businessRuleDisplayName?: string;
  onActivated?: () => Promise<void> | void;
}

interface UseActivateNotificationResult {
  activate: () => Promise<void>;
  isActivating: boolean;
  activationError: string | null;
}

const useActivateNotification = ({
  projectId,
  existingBusinessRuleId,
  hasPubsubEvent,
  businessRuleDisplayName,
  onActivated,
}: UseActivateNotificationParams): UseActivateNotificationResult => {
  const [createBusinessRule] = useCreateBusinessRuleMutation();
  const [updateBusinessRule] = useUpdateBusinessRuleMutation();
  const [addPubsubEvent] = useAddPubsubEventMutation();
  const [isActivating, setIsActivating] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);

  const activate = async (): Promise<void> => {
    if (projectId <= 0 || isActivating) {
      return;
    }

    setIsActivating(true);
    setActivationError(null);

    try {
      if (!existingBusinessRuleId) {
        await createBusinessRule({
          name: NOTIFICATION_RULE_NAME,
          displayName: businessRuleDisplayName ?? null,
          entityType: 'Project',
          entityId: projectId,
          enabled: true,
          value: DEFAULT_NOTIFICATION_CONFIG,
        }).unwrap();
      } else {
        await updateBusinessRule({
          id: existingBusinessRuleId,
          businessRule: {
            enabled: true,
          },
        }).unwrap();
      }

      if (!hasPubsubEvent) {
        await addPubsubEvent({ projectId, event: 'notifications' }).unwrap();
      }
      await onActivated?.();
    } catch (error: unknown) {
      const msg = extractErrorMessage(error, 'Error activando');
      setActivationError(msg);
      throw error;
    } finally {
      setIsActivating(false);
    }
  };

  return {
    activate,
    isActivating,
    activationError,
  };
};

export default useActivateNotification;
